import { useMemo } from 'react'
import PagedLesson from './PagedLesson'
import CopyBlock from './CopyBlock'
import FormulaBreakdown from './FormulaBreakdown'
import DataFlowDiagram from './DataFlowDiagram'
import { validateModuleSchema, normalizeAnswerIndex } from '../data/moduleSchema'
import { Info, AlertTriangle, Lightbulb, Key, ExternalLink } from 'lucide-react'

const calloutConfig = {
  tip: {
    bg: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    icon: Lightbulb,
    title: 'Pro Tip',
    iconColor: 'text-emerald-600',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: AlertTriangle,
    title: 'Watch Out',
    iconColor: 'text-amber-600',
  },
  key: {
    bg: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    icon: Key,
    title: 'Key Takeaway',
    iconColor: 'text-indigo-600',
  },
  info: {
    bg: 'bg-blue-50 border-blue-200 text-blue-900',
    icon: Info,
    title: 'Note',
    iconColor: 'text-blue-600',
  },
}

import { parseMarkdownBlocks } from '../lib/markdownUtils'

export { parseMarkdownBlocks }

/**
 * Parse inline markdown tokens: `code`, **bold**, *italic*, [link](url)
 */
export function renderInlineMarkdown(text) {
  if (!text) return ''
  const str = String(text)
  const tokenRegex = /(`[^`]+`|\*\*[^*]+?\*\*|\*[^*]+?\*|\[[^\]]+\]\([^)]+\))/g
  const parts = str.split(tokenRegex)

  return parts.map((part, i) => {
    if (!part) return null

    // Inline code: `code`
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return (
        <code
          key={i}
          className="px-1.5 py-0.5 rounded bg-slate-100 text-indigo-700 font-mono text-xs font-semibold border border-slate-200/60"
        >
          {part.slice(1, -1)}
        </code>
      )
    }

    // Bold: **bold**
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      )
    }

    // Italic: *italic*
    if (part.startsWith('*') && part.endsWith('*') && part.length >= 2) {
      return (
        <em key={i} className="italic text-slate-800">
          {part.slice(1, -1)}
        </em>
      )
    }

    // Link: [label](url)
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 underline font-medium hover:text-indigo-800"
        >
          {linkMatch[1]}
        </a>
      )
    }

    return part
  })
}

/**
 * Render a block of markdown-style text with bold, inline code, headings, lists, and paragraphs.
 */
function FormattedContent({ text = '' }) {
  if (!text) return null
  const blocks = parseMarkdownBlocks(text)

  return (
    <div className="space-y-3.5 text-sm leading-relaxed text-slate-700">
      {blocks.map((b, idx) => {
        switch (b.type) {
          case 'heading': {
            if (b.level === 1) {
              return (
                <h2 key={idx} className="text-lg font-bold text-slate-900 mt-4 mb-1">
                  {renderInlineMarkdown(b.text)}
                </h2>
              )
            }
            if (b.level === 2) {
              return (
                <h3 key={idx} className="text-base font-bold text-slate-900 mt-3 mb-1">
                  {renderInlineMarkdown(b.text)}
                </h3>
              )
            }
            return (
              <h4 key={idx} className="text-sm font-bold text-slate-900 mt-3 mb-1">
                {renderInlineMarkdown(b.text)}
              </h4>
            )
          }
          case 'quote':
            return (
              <blockquote
                key={idx}
                className="border-l-4 border-indigo-500 bg-indigo-50/60 pl-3.5 py-2 pr-3 rounded-r-xl text-slate-700 text-sm italic my-2"
              >
                {renderInlineMarkdown(b.text)}
              </blockquote>
            )
          case 'code':
            return (
              <pre
                key={idx}
                className="bg-slate-900 text-slate-100 p-3.5 rounded-xl font-mono text-xs overflow-x-auto shadow-inner my-2"
              >
                <code>{b.code}</code>
              </pre>
            )
          case 'ul':
            return (
              <ul key={idx} className="list-disc pl-5 space-y-1.5 text-slate-700 my-2">
                {b.items.map((item, i) => (
                  <li key={i}>{renderInlineMarkdown(item)}</li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={idx} className="list-decimal pl-5 space-y-1.5 text-slate-700 my-2">
                {b.items.map((item, i) => (
                  <li key={i}>{renderInlineMarkdown(item)}</li>
                ))}
              </ol>
            )
          case 'paragraph':
          default:
            return (
              <p key={idx} className="text-slate-700 leading-relaxed">
                {renderInlineMarkdown(b.text)}
              </p>
            )
        }
      })}
    </div>
  )
}

/**
 * DynamicModule accepts a JSON module schema and renders it seamlessly
 * via PagedLesson with automated scoring, question tracking, and visual elements.
 */
export default function DynamicModule({ moduleData, previewMode = false }) {
  const { normalized } = useMemo(() => validateModuleSchema(moduleData), [moduleData])

  const pages = useMemo(() => {
    if (!normalized?.pages) return []

    // Check if any pages have explicitly defined questions
    const hasPageQuestions = normalized.pages.some(
      (p) => Array.isArray(p.questions) && p.questions.length > 0
    )

    // Fallback: If root quiz.questions exists but pages don't have questions,
    // evenly distribute the root quiz questions across the pages.
    let distributedQuestions = []
    if (!hasPageQuestions && normalized.quiz?.questions?.length) {
      const totalPages = normalized.pages.length
      const rootQuestions = normalized.quiz.questions
      distributedQuestions = normalized.pages.map((_, pIdx) => {
        return rootQuestions.filter((_, qIdx) => qIdx % totalPages === pIdx)
      })
    }

    return normalized.pages.map((page, idx) => {
      const isLastPage = idx === normalized.pages.length - 1
      const callout = page.callout ? calloutConfig[page.callout.type] || calloutConfig.info : null

      const rawQuestions = hasPageQuestions
        ? page.questions || []
        : distributedQuestions[idx] || []

      const pageQuestions = rawQuestions.map((q) => {
        const options = Array.isArray(q.options) ? q.options.map((o) => String(o).trim()) : []
        return {
          question: q.question,
          options,
          correctIndex: normalizeAnswerIndex(q, options),
          explanation: q.explanation || '',
        }
      })

      const content = (
        <div className="space-y-6">
          {page.subtitle && (
            <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2">
              {page.subtitle}
            </h3>
          )}

          <FormattedContent text={page.content} />

          {/* Optional Interactive Formula Breakdown */}
          {page.formulaBreakdown && page.formulaBreakdown.formula && (
            <div className="my-5">
              <FormulaBreakdown
                formula={page.formulaBreakdown.formula}
                parts={page.formulaBreakdown.parts || []}
              />
            </div>
          )}

          {/* Optional Copyable Formula */}
          {page.formula && (
            <div className="my-4">
              <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Formula Example
              </span>
              <CopyBlock text={page.formula} label="Copy Formula" />
            </div>
          )}

          {/* Optional Data Flow Diagram */}
          {page.diagram && page.diagram.steps && (
            <div className="my-6">
              <DataFlowDiagram
                title={page.diagram.title || 'Workflow Architecture'}
                steps={page.diagram.steps}
              />
            </div>
          )}

          {/* Optional Table */}
          {/* Array.isArray, not truthiness. `rows` arrives from Firestore as
              `{ _isNestedArray: true, json: '…' }` when a caller forgets to
              hydrate it — truthy, so a `&& page.table.rows` guard passed it
              straight through to `.map` and crashed the entire module, not just
              the table. A missing table is a far better failure than a blank
              page, so the shape is checked rather than assumed. */}
          {page.table && Array.isArray(page.table.headers) && Array.isArray(page.table.rows) && (
            <div className="my-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {page.table.headers.map((h, i) => (
                      <th key={i} className="px-3.5 py-2.5 text-left font-semibold text-slate-700">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {page.table.rows.filter(Array.isArray).map((row, rIdx) => (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="px-3.5 py-2 text-slate-600">
                          {renderInlineMarkdown(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Callout Box */}
          {callout && page.callout?.text && (
            <div className={`my-4 flex items-start gap-3 rounded-xl border p-4 ${callout.bg}`}>
              <callout.icon className={`h-5 w-5 shrink-0 mt-0.5 ${callout.iconColor}`} />
              <div className="text-xs leading-relaxed">
                <span className="font-bold block mb-0.5">{callout.title}</span>
                {renderInlineMarkdown(page.callout.text)}
              </div>
            </div>
          )}

          {/* Source Doc Link if provided */}
          {isLastPage && normalized.sourceDocUrl && (
            <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
              <span>Original Source Documentation:</span>
              <a
                href={normalized.sourceDocUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-semibold"
              >
                View Source Document <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          )}
        </div>
      )

      return {
        title: page.title,
        content,
        questions: pageQuestions,
      }
    })
  }, [normalized])

  if (!normalized) {
    return (
      <div className="p-8 text-center text-slate-500">
        No module content to render.
      </div>
    )
  }

  const passingRatio = (normalized.quiz?.passingScore || 80) / 100

  return (
    <PagedLesson
      key={normalized.id || 'preview'}
      moduleId={previewMode ? `preview-${normalized.id}` : normalized.id}
      pages={pages}
      passingScore={passingRatio}
    />
  )
}
